import Editor, { type OnChange } from "@monaco-editor/react";
import { RefreshCw, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { usePutRoutingTable, useRoutingTable } from "@/lib/api/playbook";
import { intentLabel } from "@/lib/product-labels";

interface ApiErrorBody {
	detail?: string;
}

interface RoutingRulePreview {
	ruleId: string;
	intent: string;
	subTask: string;
	target: string;
	branch: string;
	mode: "routing_table" | "short_circuit";
	priority: string;
	enabled: boolean;
}

function extractErrorDetail(err: unknown): string {
	if (typeof err === "object" && err !== null) {
		const maybeAxios = err as { response?: { data?: ApiErrorBody }; message?: string };
		if (maybeAxios.response?.data?.detail) return maybeAxios.response.data.detail;
		if (maybeAxios.message) return maybeAxios.message;
	}
	return String(err);
}

function extractScalar(block: string, key: string): string {
	const match = new RegExp(`^\\s*${key}:\\s*(.+)$`, "m").exec(block);
	return (match?.[1] ?? "").replace(/^"|"$/g, "").trim();
}

function parseRoutingPreview(yaml: string): RoutingRulePreview[] {
	const rulesBlock = yaml.match(/rules:\s*\n([\s\S]+)$/)?.[1] ?? "";
	const normalized = rulesBlock
		.replace(/^\s*-\s+rule_id:\s*/m, "rule_id: ")
		.replace(/\n\s*-\s+rule_id:\s*/g, "\n---RULE---\nrule_id: ");
	const chunks = normalized.split("\n---RULE---\n").filter((chunk) => /^rule_id:/m.test(chunk));
	return chunks.map((chunk) => {
		const ruleId = extractScalar(chunk, "rule_id");
		const intent = extractScalar(chunk, "intent");
		const subTask = extractScalar(chunk, "sub_task") || "*";
		const pbId = extractScalar(chunk, "pb_id");
		const branch = extractScalar(chunk, "branch");
		const skill = extractScalar(chunk, "short_circuit_skill");
		const priority = extractScalar(chunk, "priority") || "100";
		const enabled = extractScalar(chunk, "enabled") !== "false";
		return {
			ruleId,
			intent,
			subTask,
			target: skill || pbId || "-",
			branch,
			mode: skill ? "short_circuit" : "routing_table",
			priority,
			enabled,
		};
	});
}

export function RoutingTablePanel() {
	const { data, isLoading, isError, error, refetch } = useRoutingTable();
	const putMutation = usePutRoutingTable();

	const [draft, setDraft] = useState("");
	const [loadedVersion, setLoadedVersion] = useState<string | null>(null);
	const [savedAt, setSavedAt] = useState<string | null>(null);
	const [message, setMessage] = useState<string | null>(null);
	const [unknownWarn, setUnknownWarn] = useState<string[]>([]);

	useEffect(() => {
		if (data) {
			setDraft(data.yaml_text);
			setLoadedVersion(data.bundle_version);
			setSavedAt(null);
			setMessage(null);
			setUnknownWarn([]);
		}
	}, [data]);

	const isDirty = data ? draft !== data.yaml_text : false;
	const rules = useMemo(() => parseRoutingPreview(draft), [draft]);

	const handleEditorChange: OnChange = (value) => setDraft(value ?? "");

	const handleSave = () => {
		setMessage(null);
		setSavedAt(null);
		setUnknownWarn([]);
		putMutation.mutate(
			{ yaml_text: draft, expected_version: loadedVersion },
			{
				onSuccess: (res) => {
					setLoadedVersion(res.bundle_version);
					setUnknownWarn(res.unknown_intents);
					setSavedAt(new Date().toLocaleTimeString());
				},
				onError: (err) => {
					const detail = extractErrorDetail(err);
					const status = (err as { response?: { status?: number } })?.response?.status;
					setMessage(status === 409 ? `版本冲突，请重载。${detail}` : `保存失败：${detail}`);
				},
			},
		);
	};

	const handleReload = async () => {
		const result = await refetch();
		if (result.data) {
			setDraft(result.data.yaml_text);
			setLoadedVersion(result.data.bundle_version);
			setMessage(null);
			setUnknownWarn([]);
		}
	};

	if (isLoading) {
		return (
			<div data-testid="routing-loading" className="p-4 text-sm text-text-secondary">
				加载路由规则中...
			</div>
		);
	}
	if (isError) {
		return (
			<div data-testid="routing-error" className="p-4 text-sm text-brand-danger">
				加载失败：{error instanceof Error ? error.message : String(error)}
			</div>
		);
	}

	return (
		<div className="space-y-3" data-testid="routing-table-panel">
			<div className="grid gap-3 md:grid-cols-5">
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-2">
					<div className="text-xs text-text-muted">主路由</div>
					<div className="text-sm font-semibold text-text-primary">RoutingTable 优先</div>
				</div>
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-2">
					<div className="text-xs text-text-muted">规则</div>
					<div className="text-sm font-semibold text-text-primary">
						{data?.enabled_rules ?? 0} 条启用
					</div>
				</div>
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-2">
					<div className="text-xs text-text-muted">短路径</div>
					<div className="text-sm font-semibold text-text-primary">
						{data?.short_circuit_rules ?? 0} 条 short_circuit
					</div>
				</div>
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-2">
					<div className="text-xs text-text-muted">PB 分支规则</div>
					<div className="text-sm font-semibold text-text-primary">
						{data?.branch_rules ?? 0} 条分支
					</div>
				</div>
				<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-2">
					<div className="text-xs text-text-muted">兜底</div>
					<div className="text-sm font-semibold text-text-primary">
						{data?.fallback_to_intent_playbook_mapping ? "旧 mapping 兜底" : "无兜底"}
					</div>
				</div>
			</div>

			<div className="flex flex-wrap items-center justify-between gap-2 text-sm">
				<div className="text-text-secondary">
					当前版本 <span className="font-mono text-xs">{loadedVersion ?? "-"}</span>
					{isDirty ? (
						<span className="ml-2 rounded border border-brand-warning/30 bg-brand-warning/10 px-2 py-0.5 text-xs text-brand-warning">
							草稿未保存
						</span>
					) : null}
					{savedAt ? (
						<span
							data-testid="routing-saved-at"
							className="ml-2 rounded border border-brand-success/30 bg-brand-success/10 px-2 py-0.5 text-xs text-brand-success"
						>
							已保存 {savedAt}
						</span>
					) : null}
				</div>
				<div className="flex gap-2">
					<button
						type="button"
						className="inline-flex items-center gap-1.5 rounded-md border border-surface-border px-3 py-1.5 text-sm hover:bg-surface-subtle"
						data-testid="routing-reload"
						onClick={handleReload}
					>
						<RefreshCw className="h-4 w-4" aria-hidden="true" />
						重载
					</button>
					<button
						type="button"
						className="inline-flex items-center gap-1.5 rounded-md border border-brand-accent bg-brand-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
						data-testid="routing-save"
						onClick={handleSave}
						disabled={putMutation.isPending || !isDirty}
					>
						<Save className="h-4 w-4" aria-hidden="true" />
						{putMutation.isPending ? "保存中" : "保存"}
					</button>
				</div>
			</div>

			<div className="grid gap-3 lg:grid-cols-2">
				<div className="rounded-md border border-surface-border" data-testid="routing-monaco">
					<div className="border-b border-surface-border bg-surface-subtle px-3 py-1.5 text-xs text-text-secondary">
						RoutingTable YAML
					</div>
					<Editor
						height="50vh"
						language="yaml"
						value={draft}
						onChange={handleEditorChange}
						options={{
							minimap: { enabled: false },
							fontSize: 13,
							tabSize: 2,
							wordWrap: "on",
							scrollBeyondLastLine: false,
						}}
					/>
				</div>

				<div className="rounded-md border border-surface-border" data-testid="routing-preview">
					<div className="border-b border-surface-border bg-surface-subtle px-3 py-2 text-xs text-text-secondary">
						规则预览
					</div>
					<div className="overflow-auto p-2">
						<table className="w-full border-collapse text-xs">
							<thead>
								<tr className="text-left">
									<th className="px-2 py-1 font-medium">规则</th>
									<th className="px-2 py-1 font-medium">分类</th>
									<th className="px-2 py-1 font-medium">子任务</th>
									<th className="px-2 py-1 font-medium">目标</th>
									<th className="px-2 py-1 font-medium">分支</th>
								</tr>
							</thead>
							<tbody>
								{rules.map((rule) => (
									<tr
										key={rule.ruleId}
										data-testid={`routing-row-${rule.ruleId}`}
										className="border-t border-surface-border"
									>
										<td className="px-2 py-1">
											<div className="font-mono text-[11px] text-text-primary">{rule.ruleId}</div>
											<div className="text-text-muted">P{rule.priority}</div>
										</td>
										<td className="px-2 py-1">{intentLabel(rule.intent)}</td>
										<td className="px-2 py-1">{rule.subTask}</td>
										<td className="px-2 py-1">
											<div className="flex flex-wrap items-center gap-1">
												<span
													className={
														rule.mode === "short_circuit"
															? "rounded bg-brand-info/10 px-1.5 py-0.5 font-mono text-brand-info"
															: "rounded bg-brand-success/10 px-1.5 py-0.5 font-mono text-brand-success"
													}
												>
													{rule.target}
												</span>
												<span className="text-text-muted">{rule.mode}</span>
												{rule.enabled ? null : <span className="text-brand-warning">paused</span>}
											</div>
										</td>
										<td className="px-2 py-1">
											{rule.branch ? (
												<span className="rounded-full bg-surface-subtle px-1.5 py-0.5 font-mono text-[11px] text-text-primary">
													{rule.branch}
												</span>
											) : (
												<span className="text-text-muted">PB 默认</span>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{unknownWarn.length > 0 ? (
				<div
					data-testid="routing-unknown-warning"
					className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3 text-xs text-brand-warning"
				>
					未知分类提示：{unknownWarn.join(", ")}
				</div>
			) : null}

			{message ? (
				<div
					data-testid="routing-message"
					className="rounded-md border border-brand-danger bg-brand-danger/10 p-3 text-sm text-brand-danger"
				>
					{message}
				</div>
			) : null}
		</div>
	);
}
